import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Shield, Award, Users, Target } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="About Shophentic - Our Mission & Team"
        description="Learn about Shophentic's mission to provide authentic lifestyle and tech product reviews. Meet our expert team of reviewers dedicated to helping you make informed purchasing decisions."
        keywords="about Shophentic, product review team, expert reviewers, honest reviews, buying guides, consumer advice"
        url="https://shophentic.bishalmondal.online/about"
      />
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            About Shophentic
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Your trusted destination for authentic lifestyle and tech product reviews, comprehensive buying guides, and expert recommendations to help you make informed purchasing decisions across all aspects of modern living.
          </p>
        </div>

        {/* Mission Section */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              We believe that quality products should enhance every aspect of your lifestyle, from technology to fashion, home to personal care. That's why we're dedicated to providing honest, thorough reviews and practical buying advice that cuts through the marketing noise.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our team of lifestyle and tech experts spends hundreds of hours testing products in real-world scenarios to bring you recommendations you can trust for all your daily needs.
            </p>
          </div>
          <Card className="p-6">
            <CardContent className="text-center">
              <Award className="h-16 w-16 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">5+ Years</h3>
              <p className="text-muted-foreground">Of trusted lifestyle and tech reviews and expert recommendations</p>
            </CardContent>
          </Card>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card>
            <CardHeader>
              <Shield className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Unbiased Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We maintain editorial independence and never let affiliate relationships influence our honest assessments.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Target className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Expert Testing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Our rigorous testing methodology ensures every recommendation is based on real-world performance data across lifestyle and tech products.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Users className="h-8 w-8 text-primary mb-2" />
              <CardTitle>Community First</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                We listen to our readers and continuously improve our content based on your feedback and needs.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Team Section */}
        <Card className="mb-16">
          <CardHeader>
            <CardTitle className="text-3xl text-center">Meet Our Team</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-24 h-24 bg-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">AD</span>
                </div>
                <h3 className="font-bold text-lg">Aheli Das</h3>
                <p className="text-muted-foreground">Lead Tech & Lifestyle Reviewer</p>
                <p className="text-sm text-muted-foreground mt-2">4 years in consumer products</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">BM</span>
                </div>
                <h3 className="font-bold text-lg">Bishal Mondal</h3>
                <p className="text-muted-foreground">Fashion & Mobile Expert</p>
                <p className="text-sm text-muted-foreground mt-2">3 years in lifestyle and tech</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-primary rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">AM</span>
                </div>
                <h3 className="font-bold text-lg">Arko Mondal</h3>
                <p className="text-muted-foreground">PC & Gadgets Specialist</p>
                <p className="text-sm text-muted-foreground mt-2">2 years in tech and tools</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <Card className="text-center p-8 bg-accent">
          <CardHeader>
            <CardTitle className="text-3xl">Get In Touch</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Have questions about our lifestyle and tech reviews or suggestions for products you'd like us to test? We'd love to hear from you.
            </p>
            <Button variant="default" size="lg" onClick={() => window.open('https://instagram.com/bishal.mondal45', '_blank')}>
              Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default About;